import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import { useParentsWithPhone, sendSms } from "@/hooks/useSms";

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors";
const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

export default function AdminSmsBildirim() {
  const { data: parents, loading } = useParentsWithPhone();
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; configured: boolean; text: string } | null>(null);

  const charCount = message.length;
  const segments = charCount === 0 ? 0 : Math.ceil(charCount / 160);

  const handleSend = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const p = phone.trim();
    const m = message.trim();
    if (!p || !m) {
      setResult({ ok: false, configured: true, text: "Lütfen telefon ve mesaj girin." });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const r = await sendSms(p, m);
      if (!r.configured) {
        setResult({
          ok: false,
          configured: false,
          text: "SMS servisi henüz yapılandırılmadı. Netgsm bilgilerini ekledikten sonra gönderebilirsin.",
        });
      } else if (r.ok) {
        setResult({ ok: true, configured: true, text: "SMS başarıyla gönderildi." });
      } else {
        setResult({ ok: false, configured: true, text: r.error || "SMS gönderilemedi." });
      }
    } catch {
      setResult({ ok: false, configured: true, text: "SMS gönderilirken bir hata oluştu." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="SMS Bildirimi"
        description="Velilere SMS ile bilgilendirme gönder. (Netgsm altyapısı kullanılır.)"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gönderim formu */}
        <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="text-lg font-semibold text-foreground-950">Yeni SMS Gönder</h2>
          <form onSubmit={handleSend} className="mt-4 space-y-4">
            <div>
              <label htmlFor="sms-phone" className={labelClass}>Alıcı Telefon</label>
              <input
                id="sms-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
                placeholder="Örn: 05XX XXX XX XX"
              />
            </div>
            <div>
              <label htmlFor="sms-message" className={labelClass}>
                Mesaj
                <span className="ml-2 text-xs font-normal text-foreground-400">
                  {charCount} karakter · {segments} SMS
                </span>
              </label>
              <textarea
                id="sms-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-3 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                placeholder="Veliye iletilecek mesaj..."
              />
            </div>

            {result && (
              <p
                className={`text-sm rounded-md px-3 py-2 ${
                  result.ok
                    ? "bg-accent-100 text-accent-800"
                    : result.configured
                    ? "bg-red-50 text-red-700"
                    : "bg-secondary-100 text-secondary-800"
                }`}
              >
                {result.text}
              </p>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" aria-hidden="true"></i>
                    SMS Gönder
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Kayıtlı veliler */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="text-lg font-semibold text-foreground-950">Kayıtlı Veliler</h2>
          <p className="mt-1 text-sm text-foreground-500">Telefonu kayıtlı veliler. Seçmek için tıkla.</p>

          {loading ? (
            <p className="mt-4 text-sm text-foreground-400">Yükleniyor...</p>
          ) : (parents ?? []).length === 0 ? (
            <EmptyState
              icon="ri-parent-line"
              title="Telefon kayıtlı veli yok"
              description="Velilerin telefon numarası profillerine eklendiğinde burada listelenecek."
            />
          ) : (
            <ul className="mt-4 divide-y divide-background-200">
              {(parents ?? []).map((p) => (
                <li key={p.userId}>
                  <button
                    type="button"
                    onClick={() => setPhone(p.phone)}
                    className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-background-100/60 transition-colors rounded-md px-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900 truncate">{p.name}</p>
                      <p className="text-xs text-foreground-500">{p.phone}</p>
                    </div>
                    <span className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-user-line text-sm" aria-hidden="true"></i>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}